import React, {
  VFC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';

// eslint-disable-next-line import/no-unresolved
import AuthWrapper from 'frontendAppShell/AuthWrapper';
import { isEqual } from 'lodash';
import { Router, Route, Switch } from 'wouter';

import 'mintel-react-ui/dist/package/style.css';
import 'mintel-tokens/dist/assets/fonts.css';

import CwaArticleGetter from '/components/CwaArticleGetter';
import Navigation from '/components/Navigation';
import { ContentLocation, AppShellProps } from '/types';
import { AppShellContext } from '/utils/contexts';
import { Error404 } from '/utils/HttpError';
import { i18n } from '/utils/translations';
import UserAccessRestrictionProvider from '/utils/UserAccessRestrictionProvider';

const enableMocking = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  // Enable mocking using Mock Service Worker (MSW).
  // https://mswjs.io/
  if (BUILD_TARGET !== 'localdev-mock') {
    return;
  }
  const { worker } = await import('../mocks/browser');
  return worker.start();
};

// `Setter` is an UglyHack™ TODO revert
// The `AuthWrapper` unmounts and remounts its children. This is a known issue
// with react-oidc-context v3: https://github.com/AxaGuilDEv/react-oidc/issues/541
// This is a workaround until CFE upgrade the package. The idea is to render
// `CwaArticleGetter` outside of the `AuthWrapper` and set the values to be fetched
// from within the `AuthWrapper` by the `Setter` component.
type SetterProps = ContentLocation & {
  current: ContentLocation | undefined;
  set: (params: ContentLocation) => void;
  waitForPortalJwt?: boolean;
};
const Setter: VFC<SetterProps> = ({
  set,
  current,
  waitForPortalJwt = false,
  ...params
}) => {
  const { portalJwt } = useContext(AppShellContext);

  useEffect(() => {
    if (!isEqual(current, params) && (!waitForPortalJwt || portalJwt)) {
      set(params);
    }
  }, [current, params, waitForPortalJwt, portalJwt, set]);

  return <></>;
};

const App: VFC<AppShellProps> = (props) => {
  const lang = props.accessDetails?.mntl?.lang || 'en';
  const [waitForMSW, setWaitForMSW] = useState(
    BUILD_TARGET === 'localdev-mock',
  );

  const [
    cwaArticleGetterParams,
    setCwaArticleGetterParams,
  ] = useState<ContentLocation>();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  useEffect(() => {
    if (waitForMSW) {
      enableMocking().then(() => setWaitForMSW(false));
    }
  }, [waitForMSW]);

  if (waitForMSW) return <></>;

  return (
    <AppShellContext.Provider value={props}>
      <Navigation />
      <Router base={props.path}>
        {cwaArticleGetterParams && (
          <UserAccessRestrictionProvider>
            <CwaArticleGetter {...cwaArticleGetterParams} />
          </UserAccessRestrictionProvider>
        )}
        <Switch>
          <Route path="/public/:slugOrId/:blockNum?">
            {(params): ReactElement => (
              <Setter
                current={cwaArticleGetterParams}
                set={setCwaArticleGetterParams}
                publishStatus="live"
                contentType="public"
                {...params}
              />
            )}
          </Route>

          <Route path="/preview/:contentType/:slugOrId/:blockNum?">
            {(params): ReactElement => (
              <AuthWrapper>
                <Setter
                  current={cwaArticleGetterParams}
                  set={setCwaArticleGetterParams}
                  publishStatus="preview"
                  waitForPortalJwt
                  {...params}
                />
              </AuthWrapper>
            )}
          </Route>

          <Route path="/long-term-preview/:contentType/:slugOrId/:blockNum?">
            {(params): ReactElement => (
              <AuthWrapper>
                <Setter
                  current={cwaArticleGetterParams}
                  set={setCwaArticleGetterParams}
                  publishStatus="long_term_preview"
                  waitForPortalJwt
                  {...params}
                />
              </AuthWrapper>
            )}
          </Route>

          <Route path="/:contentType/:slugOrId/:blockNum?">
            {(params): ReactElement => (
              <AuthWrapper>
                <Setter
                  current={cwaArticleGetterParams}
                  set={setCwaArticleGetterParams}
                  publishStatus="live"
                  waitForPortalJwt
                  {...params}
                />
              </AuthWrapper>
            )}
          </Route>

          <Route path="/:any*">
            <Error404 />
          </Route>
        </Switch>
      </Router>
    </AppShellContext.Provider>
  );
};

export default App;
